import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { ApiError } from '~/types';
import AuthService from '~/services/AuthService';
import { useAuthStore } from '~/store';

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// public api instance setting
export const httpPublic: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASEURL_PUBLIC,
  headers: {
    'Content-Type': 'application/json',
  },
});

// private api instance setting
const http: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASEURL_PRIVATE,
  headers: {
    'Content-Type': 'application/json',
  },
});

http.interceptors.request.use(
  async (config) => {
    try {
      let idToken = useAuthStore.getState().idToken;

      if (
        idToken &&
        (AuthService.isTokenExpiringSoon(idToken) ||
          AuthService.isTokenExpired(idToken))
      ) {
        try {
          idToken = await AuthService.refreshIdToken();
        } catch (refreshError) {
          console.error('Error pre-emptively refreshing token:', refreshError);
          await AuthService.signOut();
          window.location.href = '/signin';
          return Promise.reject(refreshError);
        }
      }

      if (idToken) {
        config.headers['Authorization'] = `Bearer ${idToken}`;
      }
    } catch (error) {
      console.error('Error fetching auth session:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

http.interceptors.response.use(
  (response) => {
    // if (response.data?.result === 99) {
    //   window.location.href = redirectUrl;
    // }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const idToken = await AuthService.refreshIdToken();
        if (idToken) {
          originalRequest.headers['Authorization'] = `Bearer ${idToken}`;
          return http(originalRequest);
        }
      } catch (refreshError) {
        console.error('Error refreshing token:', refreshError);
        await AuthService.signOut();
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    } else {
      const errorStatus = error.response?.status;
      const errorBody = error.response?.data;

      return Promise.reject({
        status: errorStatus,
        body: errorBody,
      } as ApiError);
    }

    return Promise.reject(error);
  },
);

export default http;
